import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { RouteTransition } from '../route-transition/route-transition';
import { SignupPage } from './signup/signup-page';
import { StaysSignupPage } from './signup/stays-signup-page';
import { VerifyEmailPage } from './verify-email/verify-email-page';
import { ConfirmEmailChangePage } from './verify-email/confirm-email-change-page';
import { InviteAcceptPage } from './invite/invite-accept-page';
import { PublicPassPage } from './pass/public-pass-page';
import { GuestCheckinPage } from '../pages/stays/guest/guest-checkin-page';
import { GuestGuidePage } from '../pages/stays/guest/guide/guest-guide-page';
import { StayGuestPage } from './stay/stay-guest-page';
import { StayReturnPage } from './stay/stay-return-page';
import { ForgotPasswordPage } from './password/forgot-password-page';
import { ResetPasswordPage } from './password/reset-password-page';
import { LegalPage } from '../legal/legal-page';
// IS8.0: every Stays page below is wrapped in StaysOnlyRoute; with Lobbero Stays
// switched off on the server it is not there at all.
import { StaysOnlyRoute } from './stays-only-route';

// PUBLIC routes — rendered by the root router for tokenless visitors, OUTSIDE
// AppRouter so the dashboard's data-fetching hooks never fire here.
export const PublicRoutes: React.FC = () => (
  <RouteTransition>
    {(location) => (
    <Routes location={location}>
      <Route path="/signup" element={<SignupPage />} />
      {/* Lobbero Stays sign-up (Independent Stays IS3): hosts and hotels without
          Lobbero. Closed until launch; the page then offers the waitlist. */}
      <Route path="/signup/stays" element={<StaysOnlyRoute><StaysSignupPage /></StaysOnlyRoute>} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      {/* Redeeming the link sent to a NEW address (migration 073). A separate
          page from /verify-email because it promotes a pending address over the
          live login identifier, rather than proving the one already on file. */}
      <Route
        path="/confirm-email-change"
        element={<ConfirmEmailChangePage />}
      />
      <Route path="/invite/:token" element={<InviteAcceptPage />} />
      {/* A visitor pass a resident shared with their guest (resident portal
          Plan 6). The guest has no account; a signed-in resident checking
          their own link sees it too (SIGNED_IN_PUBLIC_PREFIXES). */}
      <Route path="/p/:token" element={<PublicPassPage />} />
      {/* Lobbero Stays S3: the stay guest's check-in. /s/return FIRST — it is
          where the ID partner sends the guest back, carries no token, and
          must never be read as /s/:token with "return" for a token. */}
      <Route path="/s/return" element={<StaysOnlyRoute><StayReturnPage /></StaysOnlyRoute>} />
      {/* Lobbero Stays S6: the full check-in, one URL per step (trip,
          identity, rules, payment, done); a deep link to a later step lands
          on the current one. */}
      <Route path="/s/:token" element={<StaysOnlyRoute><GuestCheckinPage /></StaysOnlyRoute>} />
      <Route path="/s/:token/:step" element={<StaysOnlyRoute><GuestCheckinPage /></StaysOnlyRoute>} />
      {/* Lobbero Stays S7: the stay guide — the hub (/s/:token/guide) and its
          five sections (arrival, essentials, local, checkout, photos). ONE
          entry with a splat, so moving between sections keeps the page (and
          its answer) mounted; the static "guide" segment outranks S6's
          /s/:token/:step, so "guide" is never read as a check-in step. */}
      <Route path="/s/:token/guide/*" element={<StaysOnlyRoute><GuestGuidePage /></StaysOnlyRoute>} />
      <Route path="/sg/:token" element={<StaysOnlyRoute><StayGuestPage /></StaysOnlyRoute>} />
      {/* Password recovery, open to every role: the server resolves the address
          to whatever account holds it and never asks what kind of user it is. */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* The policies — privacy, terms, cookies, DPA, security. Signed in or
          out (see SIGNED_IN_PUBLIC_PREFIXES). A bare /legal opens privacy. */}
      <Route path="/legal" element={<Navigate to="/legal/privacy" replace />} />
      <Route path="/legal/:doc" element={<LegalPage />} />
      {/* NOTHING MATCHED, SIGNED OUT.
          `isPublicPath` matches on a bare prefix, so /invite with its token
          lost — the single most likely way to mangle one of these links —
          was handed to this router, matched no route, and rendered an empty
          page. A signed-out visitor cannot even get to the not-found screen
          inside the app, so this needs its own. Sign-in is the only place
          they can usefully go. */}
      {/* AND IT SAYS SO WHEN IT ARRIVES. The bare redirect was correct and
          silent: a visitor who mistyped a link, or followed an expired one,
          simply found themselves on a sign-in form with no account of why, which
          reads as having been signed out rather than as having gone nowhere.
          The flag lets Sign In add one line; it deliberately does not claim the
          address was wrong, because a private address that needs a session
          lands here too. */}
      <Route
        path="*"
        element={<Navigate to="/" replace state={{ unmatched: true }} />}
      />
    </Routes>
    )}
  </RouteTransition>
);
